import {
  Trophy,
  TrendingDown,
  ArrowRightLeft,
  UserCog,
  UserPlus,
  MessageSquare,
  AlertTriangle,
  CalendarClock,
  Hourglass,
  AtSign,
  Settings,
  Bell,
  MessageSquareQuote,
  type LucideIcon,
} from "lucide-react";
import type { Notification } from "@/hooks/app/useNotifications";

type Tone = "success" | "danger" | "warning" | "info" | "neutral";

const TONE_CLASS: Record<Tone, string> = {
  success: "bg-success/10 text-success",
  danger: "bg-destructive/10 text-destructive",
  warning: "bg-warning/10 text-warning",
  info: "bg-primary/10 text-primary",
  neutral: "bg-muted text-muted-foreground",
};

// Barra lateral só sinaliza exceção: urgente/alta. Medium/low sem barra —
// prioridade "normal" pintada de amarelo virava ruído e igualava high a medium.
const PRIORITY_BAR: Record<string, string> = {
  urgent: "bg-destructive",
  high: "bg-warning",
  medium: "bg-transparent",
  low: "bg-transparent",
};

/**
 * Ícone + tom visual derivados do event_key/category (v2). Para linhas legadas
 * (sem taxonomia v2) cai em fallbacks por entity_type, action_url e substrings do
 * `type`, evitando que notificações reais virem o ícone genérico de engrenagem.
 */
export function getNotificationVisual(
  n: Pick<Notification, "event_key" | "category" | "type" | "entity_type" | "action_url">
): {
  Icon: LucideIcon;
  toneClass: string;
} {
  const key = (n.event_key ?? n.type ?? "").toLowerCase();
  const cat = (n.category ?? key.split(/[._]/)[0] ?? "").toLowerCase();
  const entity = (n.entity_type ?? "").toLowerCase();
  const url = (n.action_url ?? "").toLowerCase();
  const hay = `${key} ${cat} ${entity} ${url}`;

  let Icon: LucideIcon = Bell;
  let tone: Tone = "neutral";

  if (key === "deal.won" || key === "deal_won") { Icon = Trophy; tone = "success"; }
  else if (key === "deal.lost" || key === "deal_lost") { Icon = TrendingDown; tone = "danger"; }
  else if (key === "deal.stalled") { Icon = Hourglass; tone = "warning"; }
  else if (key === "lead.assigned") { Icon = UserPlus; tone = "info"; }
  else if (cat === "coaching") { Icon = MessageSquareQuote; tone = "info"; }
  else if (cat === "deal" || entity === "deal") { Icon = ArrowRightLeft; tone = "info"; }
  else if (cat === "lead" || entity === "lead") { Icon = UserCog; tone = "info"; }
  else if (key.includes("overdue")) { Icon = AlertTriangle; tone = "danger"; }
  else if (cat === "activity" || entity === "activity") { Icon = CalendarClock; tone = "warning"; }
  else if (
    cat === "atendimento" ||
    entity === "conversation" ||
    url.includes("/chat") ||
    hay.includes("unanswered") ||
    hay.includes("conversa")
  ) { Icon = MessageSquare; tone = "info"; }
  else if (cat === "mention" || hay.includes("mention")) { Icon = AtSign; tone = "info"; }
  else if (cat === "system") { Icon = Settings; tone = "neutral"; }

  return { Icon, toneClass: TONE_CLASS[tone] };
}

export function getPriorityBarClass(priority: string): string {
  return PRIORITY_BAR[priority] ?? "bg-transparent";
}

/** Contador de eventos agrupados (rollup por group_key no backend). */
export function getRollupCount(n: Pick<Notification, "metadata">): number {
  const count = Number(n.metadata?.count);
  return Number.isFinite(count) && count > 1 ? count : 0;
}

/** Rota interna de destino. Prefere entity_type/entity_id (robusto); cai no action_url. */
export function getNotificationRoute(
  n: Pick<Notification, "entity_type" | "entity_id" | "action_url">
): string | null {
  if (n.entity_type) {
    if (n.entity_id) {
      switch (n.entity_type) {
        case "deal": return `/deals/${n.entity_id}`;
        case "lead": return `/leads/${n.entity_id}`;
        case "conversation": return `/chat?conversation=${n.entity_id}`;
        case "activity": return `/activities?highlight=${n.entity_id}`;
      }
    } else {
      // Rollup agregado sem entidade única (ex.: deal.stalled por owner) → lista.
      switch (n.entity_type) {
        case "deal": return "/deals";
        case "lead": return "/leads";
        case "conversation": return "/chat";
        case "activity": return "/activities";
      }
    }
  }
  const url = n.action_url ?? null;
  if (!url) return null;
  if (url.startsWith("/")) return url;
  // action_url absoluto: same-origin vira path interno; externo é descartado
  // (navigate() trataria "https://..." como rota relativa e quebraria).
  try {
    const parsed = new URL(url);
    if (typeof window !== "undefined" && parsed.origin === window.location.origin) {
      return parsed.pathname + parsed.search;
    }
  } catch {
    // action_url malformado — ignora
  }
  return null;
}
